<template>
  <aside :class="['sidebar', { 'sidebar-collapsed': isCollapsed }]" role="navigation"
    aria-label="Menu principal de navegação">
    <div class="logo">
      <img v-if="!isCollapsed" class="logo-default" src="/logo-2.png" alt="Logo da autoescola" width="124"
        height="auto">
      <img v-else class="logo-collapsed" src="/logo-collapsed.svg" alt="Logo da autoescola (versão reduzida)" width="52"
        height="auto">

      <button @click="toggleCoreSidebar" class="toggle-button"
        :aria-label="isCollapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'" type="button">
        <i class="fa fa-chevron-circle-left" aria-hidden="true"></i>
      </button>
    </div>

    <nav class="menu" role="menubar" aria-label="Navegação principal">
      <!-- Template unificado para todos os roles -->
      <template v-for="(link, index) in currentUserLinks()">
        <SidebarLink :key="'link-' + index" v-if="link.path && link.condition" :link="link.path" :linkText="link.text"
          :linkIcon="link.icon" :isCollapsed="isCollapsed" @toggleSidebar="openSidebar" />

        <SidebarGroup :key="'group-' + index" v-else-if="!link.path && link.condition" :linkText="link.text"
          :linkIcon="link.icon" :isCollapsed="isCollapsed" :dropdownLinks="link.dropdownLinks"
          @toggleSidebar="openSidebar" />
      </template>
    </nav>
  </aside>
</template>

<script>
import SidebarGroup from './sidebar-group.vue';
import SidebarLink from './sidebar-link.vue';
import { USER_ROLES, canAccessTelegram, canSendNotifications } from '@/constants/userRoles';
import { useAuth } from '@/composables/useAuth';

export default {
  components: {
    SidebarLink,
    SidebarGroup,
  },
  props: {
    isCollapsed: {
      type: Boolean,
      required: true
    },
  },
  data() {
    return {
      roles: JSON.parse(localStorage.permissions),
      cfc: localStorage.cfc,
      email: localStorage.email,
      idRole: localStorage.id_role,
      isMobile: false,
      auth: useAuth(),
      USER_ROLES: USER_ROLES,
      monitoramentoSpecialEmails: ['nadlo.cavalcante@fabricainfo.com', 'nicole@fabricainfo.com', 'levi@fabricainfo.com', 'jvdsantos@teste.com', 'brenda.tavares@monitoramento.com'],
    };
  },
  computed: {
    schema() {
      // Computed reativo para sempre pegar o valor atual do localStorage
      return localStorage.schema;
    }
  },
  methods: {
    openSidebar() {
      this.$emit('openCoreSidebar')
    },
    toggleCoreSidebar() {
      this.$emit('toggleCoreSidebar')
    },
    // Helper methods para controle de acesso baseado em roles
    canSendNotifications() {
      // Usa função centralizada das constantes
      return canSendNotifications(this.idRole)
    },
    canAccessTelegramDashboard() {
      // NOVA REGRA: Exibir somente para usuários com id_role = 2 (Monitor),
      // independentemente do schema do usuário.
      return parseInt(this.idRole) === USER_ROLES.MONITOR;
    },
    isAutoescolaUser() {
      // Roles que representam usuários da autoescola
      const autoescolaRoles = [
        this.USER_ROLES.INSTRUTOR,
        this.USER_ROLES.GESTOR,
        this.USER_ROLES.FINANCEIRO,
        this.USER_ROLES.COMERCIAL
      ]
      return autoescolaRoles.includes(parseInt(this.idRole))
    },
    // Computed principal que retorna os links baseado no role do usuário
    currentUserLinks() {
      // Mapeia os roles para seus respectivos links usando a estrutura original
      if (this.idRole == USER_ROLES.FINANCEIRO) {
        return this.financeiroLinks();
      } else if (this.idRole == USER_ROLES.MONITOR) {
        return this.monitoramentoLinks();
      } else if (this.idRole == USER_ROLES.ALUNO) {
        return this.alunoLinks();
      } else if (this.idRole == USER_ROLES.COMERCIAL) {
        return this.comercialLinks();
      } else if (this.idRole == USER_ROLES.DETRAN) {
        return this.detranLinks();
      } else {
        return this.adminLinks();
      }
    },
    adminLinks() {
      const links = [
        {
          path: { name: 'dashboard' },
          text: 'Dashboard',
          icon: 'dashboard',
          condition: true,
        },
        {
          text: 'Alunos',
          icon: 'alunos',
          condition: this.roles.alunos_list || this.roles.alunos_edit,
          dropdownLinks: this.adminAlunosLinks(),
        },
        {
          text: 'Aulas práticas',
          icon: 'praticas',
          condition: this.roles.agendamento || this.roles.aulas_list || this.roles.planos,
          dropdownLinks: this.adminPraticasLinks(),
        },
        {
          text: 'Aulas teóricas',
          icon: 'teoricas',
          condition: this.roles.turmas_edit || this.roles.turmas_list,
          dropdownLinks: this.adminTeoricasLinks(),
        },
        {
          text: 'CFCs',
          icon: 'autoescolas',
          condition: this.cfc == 'public' && this.roles.cfcs,
          dropdownLinks: this.adminCfcsLinks(),
        },
        {
          text: 'Faturas',
          icon: 'faturas',
          condition: this.roles.faturas_teoricas || this.roles.faturas_praticas,
          dropdownLinks: this.adminFaturasLinks(),
        },
        {
          text: 'Instrutores',
          icon: 'instrutores',
          condition: this.roles.instrutor_list || this.roles.instrutor_edit,
          dropdownLinks: this.adminInstrutoresLinks(),
        },
        {
          text: 'Monitoramento',
          icon: 'monitoramento',
          condition: this.roles.monitoramento_teorica || this.roles.monitoramento_relatorios,
          dropdownLinks: this.adminMonitoramentoLinks(),
        },
        {
          text: 'Relatórios',
          icon: 'relatorios',
          condition: this.roles.relatorios,
          dropdownLinks: this.adminRelatoriosLinks(),
        },
        {
          text: 'Usuários',
          icon: 'usuarios',
          condition: this.roles.user_list || this.roles.user_edit || this.roles.permissoes,
          dropdownLinks: this.adminUsuariosLinks(),
        },
        {
          text: 'Veículos',
          icon: 'veiculos',
          condition: this.roles.veiculo_list || this.roles.veiculo_edit,
          dropdownLinks: this.adminVeiculosLinks(),
        },
        {
          text: 'Avisos',
          icon: 'avisos',
          condition: true,
          dropdownLinks: this.avisosLinks(),
        },
        {
          path: { name: 'transactions' },
          text: 'Transações',
          icon: 'faturas',
          condition: true,
        },
      ]

      return links
    },
    financeiroLinks() {
      const links = [
        {
          path: { name: 'transactions_financeiro' },
          text: 'Transações',
          icon: 'faturas',
          condition: true,
        },
        {
          path: { name: 'faturas_boletos' },
          text: 'Faturamento AE',
          icon: 'dashboard',
          condition: true,
        },
        {
          path: { name: 'faturas_cadastradas' },
          text: 'Alunos Faturados',
          icon: 'perfil',
          condition: true,
        },
        {
          path: { name: 'Listar_alunos' },
          text: 'Alunos Cadastradas',
          icon: 'perfil',
          condition: true,
        },
        {
          path: { name: 'relatorios' },
          text: 'Relatórios',
          icon: 'relatorios',
          condition: true,
        },
        {
          text: 'Faturamento',
          icon: 'faturamento',
          condition: true,
          dropdownLinks: [
            { text: 'Faturamento', link: { name: 'faturamento-panel' } },
            { text: 'Importar Faturas', link: { name: 'importarFaturas-financeiro' } },
            { text: 'Histórico de Baixas Manuais', link: { name: 'historico-baixa-manual' } },
          ],
        },
        {
          path: { name: 'autoescolas' },
          text: 'Autoescolas',
          icon: 'autoescolas',
          condition: true,
        },
        {
          text: 'Sindicato',
          icon: 'sindicato',
          condition: true,
          dropdownLinks: [
            { text: 'Recebimento', link: { name: 'sindicato-recebimentos' } },
          ],
        },
        {
          text: 'Cobranças Extras',
          icon: 'faturas',
          condition: true,
          dropdownLinks: [
            { text: 'Serviços/Produto', link: { name: 'produtos-servicos' } },
            { text: 'Criar Cobrança', link: { name: 'criar-cobranca' } },
            { text: 'Buscar Cobranças', link: { name: 'buscar-cobranca' } },
          ],
        },
        {
          text: 'Creditos',
          icon: 'faturas',
          condition: true,
          dropdownLinks: [
            { text: 'Créditos', link: { name: 'creditos' } },
            // { text: 'Transações', link: { name: 'transactions' } },
          ],
        },
      ]

      return links
    },
    monitoramentoLinks() {
      const links = [
        {
          path: { name: 'DashboardMonitoramento' },
          text: 'Dashboard',
          icon: 'dashboard',
          condition: true,
        },
        {
          path: { name: 'transactions' },
          text: 'Transações',
          icon: 'faturas',
          condition: true,
        },
        {
          path: { name: 'ListarAlunos' },
          text: 'Alunos',
          icon: 'alunos',
          condition: true,
        },
        {
          text: 'Práticas',
          icon: 'praticas',
          condition: true,
          dropdownLinks: this.monitoramentoPraticaLinks()
        },
        {
          path: { name: 'ListarTurmasTeoricas' },
          text: 'Turmas teóricas',
          icon: 'teoricas',
          condition: true,
        },
        {
          path: { name: 'ListarAulasVirtuais' },
          text: 'Monitorar teóricas',
          icon: 'monitoramento',
          condition: true,
        },
        {
          text: 'Cargas horárias',
          icon: 'cargas-horarias',
          condition: true,
          dropdownLinks: this.monitoramentoCargaHorariaLinks(),
        },
        {
          path: { name: 'ListarInstrutores' },
          text: 'Instrutores',
          icon: 'instrutores',
          condition: true,
        },
        {
          path: { name: 'ListarVeiculos' },
          text: 'Veículos',
          icon: 'veiculos',
          condition: true,
        },
        {
          text: 'Avisos',
          icon: 'avisos',
          condition: true,
          dropdownLinks: this.avisosLinks(),
        },
        {
          text: 'Relatórios',
          icon: 'relatorios',
          condition: this.roles.relatorios,
          dropdownLinks: this.adminRelatoriosLinks(),
        },
        {
          path: { name: 'ListarSalas' },
          text: 'Salas',
          icon: 'salas',
          condition: true,
        },
        {
          text: 'CFCs',
          icon: 'autoescolas',
          condition: this.cfcsMonitoramentoCondition(),
          dropdownLinks: this.adminCfcsLinks(),
        },
        {
          text: 'Usuários',
          icon: 'usuarios',
          condition: this.usersMonitoramentoCondition(),
          dropdownLinks: this.adminUsuariosLinks(),
        },
        {
          text: 'Auditoria',
          icon: 'monitoramento',
          condition: true,
          dropdownLinks: this.monitoramentoAuditoriaLinks(),
        },
      ]

      const adminUser = ['demys@fabricainfo.com', 'nadlo.cavalcante@fabricainfo.com', 'nicole@monitoramento.com', 'wylder@monitoramento.com', 'brenda.tavares@monitoramento.com']
      if (adminUser.includes(localStorage.email)) {
        links.push({
          path: { name: 'ConfiguracaoAdm' },
          text: 'Configurações administrativas',
          icon: 'configuracoes',
          condition: true,
        })
      }

      return links
    },
    alunoLinks() {
      const links = [
        {
          path: { name: 'aulas-teoricas' },
          text: 'Aulas teóricas',
          icon: 'teoricas',
          condition: true,
        },
        {
          path: { name: 'aulas-praticas' },
          text: 'Aulas práticas',
          icon: 'praticas',
          condition: true,
        },
      ]

      return links
    },
    comercialLinks() {
      const links = [
        {
          path: { name: 'lista-autoescolas' },
          text: 'Relação de autoescolas',
          icon: 'autoescolas',
          condition: true,
        },
        {
          path: { name: 'ListaAlunos' },
          text: 'Alunos',
          icon: 'alunos',
          condition: true,
        },
        {
          path: { name: 'RelatorioFinanceiro' },
          text: 'Relatório financeiro',
          icon: 'relatorios-financeiro',
          condition: true,
        },
        {
          path: { name: 'RelatorioMonitoramento' },
          text: 'Relatório Monitoria',
          icon: 'faturamento',
          condition: true,
        }
      ]

      return links
    },
    detranLinks() {
      const links = [
        {
          path: { name: 'Getran-listarAlunos' },
          text: 'Alunos',
          icon: 'alunos',
          condition: true,
        },
        {
          path: { name: 'Getran-ListarAulasPraticas' },
          text: 'Aulas práticas',
          icon: 'praticas',
          condition: true,
        },
        {
          path: { name: 'Getran-ListarTurmasTeoricas' },
          text: 'Aulas teóricas',
          icon: 'teoricas',
          condition: true,
        },
        {
          path: { name: 'Getran-ListarAlertas' },
          text: 'Alertas',
          icon: 'alertas',
          condition: true,
        },
        {
          path: { name: 'Getran-ListarInstrutores' },
          text: 'Instrutores',
          icon: 'instrutores',
          condition: true,
        },
        {
          path: { name: 'Getran-ListarVeiculos' },
          text: 'Veículos',
          icon: 'veiculos',
          condition: true,
        },
      ]

      return links
    },
    monitoramentoCargaHorariaLinks() {
      const links = [
        { text: 'Carga horária teórica', link: { name: 'carga-horaria-teorica' } },
      ]

      if (this.roles.agendamento) {
        links.push({ text: 'Carga horária prática', link: { name: 'processo-agendamento' } })
      }

      return links
    },
    avisosLinks() {
      const links = []

      // Nova notificação apenas para monitores
      if (this.canSendNotifications()) {
        links.push({ text: 'Nova notificação', link: { name: 'Avisos' } })
      }

      // Painel de Monitoramento - Telegram apenas para usuários internos do NovoCFC
      // ✅ REGRA: Apenas Admins (id_funcao = 1) dos schemas "teste" ou "public"
      // ❌ NÃO APARECE: Para autoescolas, outros schemas ou outras funções
      if (this.canAccessTelegramDashboard()) {
        links.push({ text: 'Painel de Monitoramento - Telegram', link: { name: 'TelegramDashboard' } })
      }

      // Central de Notificações para todos
      links.push({ text: 'Central de Notificações', link: { name: 'notifications' } })

      return links
    },
    adminAlunosLinks() {
      const links = []

      if (this.roles.alunos_edit) {
        links.push({ text: 'Novo Aluno', link: { name: 'novo-aluno-basic' } })
      }

      if (this.auth.canUseIntegracaoDetran) {
        links.push({ text: 'Novo aluno com LADV', link: { name: 'emissao-ladv' } })
      }

      if (this.roles.alunos_list) {
        links.push({ text: 'Listar alunos', link: { name: 'listagem-alunos' } })
      }

      return links
    },
    adminPraticasLinks() {
      const links = [
        { text: 'Cadastro do perímetro', link: { name: 'perimetro-index' } },
      ]

      if (this.roles.agendamento) {
        links.push({ text: 'Consultar Carga Horária', link: { name: 'processo-agendamento' } })
        links.push({ text: 'Buscar Aulas Práticas', link: { name: 'processo-index' } })
        links.push({ text: 'Agendar aula prática', link: { name: 'agendar-pratica' } })
      }

      return links
    },
    adminTeoricasLinks() {
      const links = []

      if (this.roles.agendamento) {
        links.push({ text: 'Consultar Carga Horária', link: { name: 'carga-horaria-teorica' } })
      }

      if (this.roles.turmas_list) {
        links.push({ text: 'Listar turmas', link: { name: 'listagem-turmas' } })
        links.push({ text: 'Aulas Presenciais', link: { name: 'aulas-presenciais' } })
      }

      return links
    },
    adminCfcsLinks() {
      const links = []

      if (this.roles.cfcs) {
        links.push({ text: 'Listar CFCs', link: { name: 'cfc' } })
        links.push({ text: 'Novo cadastro', link: { name: 'cfc-novo' } })
      }

      return links
    },
    adminFaturasLinks() {
      const links = []

      if (this.roles.faturas_teoricas) {
        links.push({ text: 'Faturas', link: { name: 'faturas-index' } })
        links.push({ text: 'Importar faturas', link: { name: 'importarFaturas' } })
        links.push({ text: 'Cobranças Extras', link: { name: 'buscar-cobranca-admin' } })
      }

      return links
    },
    adminInstrutoresLinks() {
      const links = []

      if (this.roles.faturas_teoricas) {
        links.push({ text: 'Listar instrutores', link: { name: 'listagem-instrutores' } })
      }

      return links
    },
    adminMonitoramentoLinks() {
      const links = []

      if (this.roles.id_permissao == 1 || this.roles.id_permissao == 2) {
        links.push({ text: 'Monitorar aulas virtuais', link: { name: 'monitoramento-virtual' } })
      }

      if (this.roles.monitoramento_relatorios) {
        links.push({ text: 'Relatórios', link: { name: 'relatorio-monitoramento' } })
        links.push({ text: 'Alertas', link: { name: 'alertas' } })
      }

      return links
    },
    adminRelatoriosLinks() {
      const links = []

      if (this.idRole == 2) {
        links.push({ text: 'Monitoria', link: { name: 'ListarRelatorios' } })
      }

      if ((this.roles.relatorios && this.idRole == 1) || (this.roles.relatorios && this.idRole == 2 && this.monitoramentoSpecialEmails.includes(this.email))) {
        links.push({ text: 'Geral', link: { name: 'geral' } })
        links.push({ text: 'Ações dos Usuários', link: { name: 'acoes-usuarios' } })
      }

      if (this.idRole == 2 && this.monitoramentoSpecialEmails.includes(this.email)) {
        links.push({ text: 'Faturamento', link: { name: 'faturamento' } })
      }

      return links
    },
    adminUsuariosLinks() {
      const links = []

      if (this.roles.user_edit) {
        links.push({ text: 'Novo usuário', link: { name: 'novo-usuario' } })
      }

      if (this.roles.user_list) {
        links.push({ text: 'Listar usuários', link: { name: 'listagem-usuarios' } })
      }

      if (this.roles.permissoes) {
        links.push({ text: 'Permissões', link: { name: 'permissoes' } })
      }

      return links
    },
    adminVeiculosLinks() {
      const links = []

      if (this.roles.veiculo_list) {
        links.push({ text: 'Listar veículos', link: { name: 'listagem-veiculos' } })
      }

      return links
    },
    monitoramentoPraticaLinks() {
      const links = []

      links.push({ text: 'Aulas Práticas', link: { name: 'ListarAulasPraticas' } })
      links.push({ text: 'Agendar aula prática', link: { name: 'agendar-pratica' } })

      if (this.idRole == 2 || this.idRole == 7) {
        links.push({ text: 'Envio de aulas', link: { name: 'MonEnvioAulasPraticas' } })
      }

      return links
    },
    cfcsMonitoramentoCondition() {
      return this.idRole == 2 && this.roles.cfcs && this.monitoramentoSpecialEmails.includes(this.email)
    },
    usersMonitoramentoCondition() {
      return (this.roles.user_list || this.roles.user_edit || this.roles.permissoes) && this.monitoramentoSpecialEmails.includes(this.email)
    },
    monitoramentoAuditoriaLinks() {
      const links = []

      links.push({ text: 'Aulas', link: { name: 'auditoria-aulas' } })

      return links
    }
  }
};
</script>

<style lang="scss" scoped>
.sidebar {
  width: 15rem;
  height: 100vh;
  background-color: #1C2D5A;
  color: white;
  box-sizing: border-box;
  z-index: 1000;
  transition: width 0.3s ease;

  @media only screen and (max-width: 768px) {
    position: fixed;
    transition: width 0.3s ease;
  }
}

.sidebar-collapsed {
  width: 5.3125rem;
  transition: width 0.3s ease;

  @media only screen and (max-width: 768px) {
    display: none;
  }
}

.logo {
  padding: 1.75rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  img.logo-default {
    width: 7.75rem;
    max-width: 100%;
    height: auto;
  }

  img.logo-collapsed {
    width: 3.25rem;
    max-width: 100%;
    height: auto;
  }

  .toggle-button {
    display: none;
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 0;
    margin: 0;

    &:hover,
    &:focus {
      outline: 2px solid rgba(255, 255, 255, 0.3);
      outline-offset: 2px;
    }
  }

  @media only screen and (max-width: 768px) {
    .toggle-button {
      display: flex;
      position: relative;
      right: -3.5rem;
      width: 1.625rem;
      height: 1.625rem;
      background: #1C2D5A;
      border-radius: 9999px;
      justify-content: center;
      align-items: center;
      color: white;

      i {
        font-size: 22px;
      }

      &:hover,
      &:focus {
        background: lighten(#1C2D5A, 10%);
      }
    }
  }
}

.menu {
  list-style: none;
  max-height: 90vh;
  padding: 0 1rem;

  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 0;
    height: 0;
  }

  scrollbar-width: none;
}

.menu {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.menu::-webkit-scrollbar {
  display: none;
}

.dropdown {
  padding-left: 20px;
  margin-top: 10px;
}

.dropdown a {
  font-size: 14px;
}

.toggle-button {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 20px;
  margin-top: 20px;
}
</style>
